import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { payslipService } from '../services/api';
import { FileText, Download, Calendar, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import pdfService from '../services/pdfService';
import toast from 'react-hot-toast';

const MyPayslips = () => {
  const { user } = useAuth();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewPayslipId, setViewPayslipId] = useState(null);
  const [showAnnualReportModal, setShowAnnualReportModal] = useState(false);

  // Buscar holerites aprovados do funcionário
  const { data: payslipsData } = useQuery({
    queryKey: ['my-payslips', selectedYear, user?.id],
    queryFn: () => payslipService.getPayslips({
      year: selectedYear,
      status: 'APPROVED',
    }),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Buscar informe de rendimentos anual (DIRF / eSocial)
  const { data: annualReportData, isLoading: loadingAnnualReport } = useQuery({
    queryKey: ['annual-report', user?.id, selectedYear],
    queryFn: () => payslipService.getAnnualEarningsReport(user?.id, { year: selectedYear }),
    enabled: !!user?.id && showAnnualReportModal,
  });

  const annualReport = annualReportData?.data?.data || null;

  const payslips = useMemo(() => {
    if (!payslipsData?.data?.data) return [];
    const all = Array.isArray(payslipsData.data.data) ? payslipsData.data.data : [];
    // Filtrar apenas os holerites do usuário logado
    return all.filter(p => p.user_id === user?.id);
  }, [payslipsData, user?.id]);

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meus Holerites</h1>
          <p className="text-gray-600">Visualize seus holerites aprovados</p>
        </div>
        <button
          type="button"
          onClick={() => setShowAnnualReportModal(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-semibold flex items-center gap-2 transition-colors shadow-sm self-start sm:self-auto"
        >
          <FileText className="w-4 h-4" />
          Informe de Rendimentos ({selectedYear})
        </button>
      </div>

      {/* Filtro de ano */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Ano</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista de holerites */}
      {payslips.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Nenhum holerite encontrado para o ano selecionado.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {payslips.map((payslip) => (
            <div
              key={payslip.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      {monthNames[payslip.ref_month - 1]} / {payslip.ref_year}
                    </p>
                    <p className="text-xs text-gray-500">Aprovado em {new Date(payslip.approved_at).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>
                {payslip.paid_at ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold">
                    Pago ✓
                  </span>
                ) : (
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    Aprovado
                  </span>
                )}
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-gray-600 mb-1">Total</p>
                <p className="text-2xl font-bold text-green-600">
                  {Number(payslip.total_salary || 0).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </p>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      // Buscar holerite completo com todos os detalhes antes de gerar PDF
                      const fullPayslip = await payslipService.getPayslip(payslip.id);
                      if (fullPayslip?.data?.data) {
                        pdfService.generatePayslipPDF(fullPayslip.data.data);
                        toast.success('PDF do holerite gerado com sucesso!');
                      } else {
                        toast.error('Erro ao buscar dados completos do holerite');
                      }
                    } catch (error) {
                      toast.error('Erro ao gerar PDF do holerite');
                      console.error(error);
                    }
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center justify-center"
                  title="Baixar PDF"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de visualização */}
      {viewPayslipId && (
        <PayslipViewModal
          payslipId={viewPayslipId}
          onClose={() => setViewPayslipId(null)}
        />
      )}

      {/* Modal de Informe de Rendimentos Anual / DIRF */}
      {showAnnualReportModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-w-3xl w-full p-6 space-y-5 my-8">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2 text-indigo-700 font-bold text-lg">
                <FileText className="w-5 h-5" />
                Informe de Rendimentos Anual (DIRF / eSocial) - Ano {selectedYear}
              </div>
              <button
                type="button"
                onClick={() => setShowAnnualReportModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingAnnualReport ? (
              <div className="p-8 text-center text-gray-500">Carregando informe de rendimentos...</div>
            ) : annualReport ? (
              <div className="space-y-4 text-sm">
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div><strong>Beneficiário:</strong> {annualReport.employee.name}</div>
                  <div><strong>Cargo:</strong> {annualReport.employee.role || 'Não Informado'}</div>
                  <div><strong>Admissão:</strong> {annualReport.employee.hireDate ? new Date(annualReport.employee.hireDate).toLocaleDateString('pt-BR') : '-'}</div>
                  <div><strong>Competência:</strong> Ano-Calendário {annualReport.year}</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3.5 bg-blue-50/70 border border-blue-200 rounded-xl">
                    <span className="text-xs text-blue-700 font-semibold uppercase tracking-wider block mb-1">Rendimentos Tributáveis</span>
                    <span className="text-xl font-bold text-blue-950">
                      {Number(annualReport.quadroRendimentos.rendimentosTributaveis || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                    <span className="block text-[11px] text-blue-700 mt-1">Salários, horas extras e adicionais</span>
                  </div>

                  <div className="p-3.5 bg-emerald-50/70 border border-emerald-200 rounded-xl">
                    <span className="text-xs text-emerald-700 font-semibold uppercase tracking-wider block mb-1">Previdência Oficial (INSS)</span>
                    <span className="text-xl font-bold text-emerald-950">
                      {Number(annualReport.quadroRendimentos.previdenciaOficial || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                    <span className="block text-[11px] text-emerald-700 mt-1">Total retido na fonte no ano</span>
                  </div>

                  <div className="p-3.5 bg-amber-50/70 border border-amber-200 rounded-xl">
                    <span className="text-xs text-amber-700 font-semibold uppercase tracking-wider block mb-1">13º Salário (Trib. Exclusiva)</span>
                    <span className="text-xl font-bold text-amber-950">
                      {Number(annualReport.quadroRendimentos.decimoTerceiro || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                    <span className="block text-[11px] text-amber-700 mt-1">1ª e 2ª parcelas do 13º</span>
                  </div>

                  <div className="p-3.5 bg-purple-50/70 border border-purple-200 rounded-xl">
                    <span className="text-xs text-purple-700 font-semibold uppercase tracking-wider block mb-1">Rendimentos Isentos / Não Trib.</span>
                    <span className="text-xl font-bold text-purple-950">
                      {Number(annualReport.quadroRendimentos.rendimentosIsentos || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                    <span className="block text-[11px] text-purple-700 mt-1">Indenizações rescisórias / abonos</span>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl overflow-hidden mt-3">
                  <div className="p-3 bg-gray-50 border-b border-gray-200 font-semibold text-xs text-gray-700 flex justify-between">
                    <span>Extrato Mensal da Competência ({annualReport.breakdownByMonth.length} meses apurados)</span>
                    <span>{annualReport.totalHoleritesAprovados} holerite(s) oficializados</span>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                        <tr>
                          <th className="px-3 py-2">Mês</th>
                          <th className="px-3 py-2">Tipo</th>
                          <th className="px-3 py-2 text-right">Bruto (R$)</th>
                          <th className="px-3 py-2 text-right">INSS (R$)</th>
                          <th className="px-3 py-2 text-right">Líquido (R$)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {annualReport.breakdownByMonth.map((m, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-1.5 font-medium">{String(m.month).padStart(2, '0')}/{annualReport.year}</td>
                            <td className="px-3 py-1.5 text-gray-600">{m.type}</td>
                            <td className="px-3 py-1.5 text-right font-medium">{Number(m.gross || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                            <td className="px-3 py-1.5 text-right text-red-600">-{Number(m.inss || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                            <td className="px-3 py-1.5 text-right font-bold text-emerald-700">{Number(m.net || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">Nenhum dado encontrado para este ano.</div>
            )}

            <div className="flex justify-end pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowAnnualReportModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-semibold transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Modal de visualização de holerite
const PayslipViewModal = ({ payslipId, onClose }) => {
  const { data: payslipData } = useQuery({
    queryKey: ['payslip', payslipId],
    queryFn: () => payslipService.getPayslip(payslipId),
    enabled: !!payslipId,
  });

  const payslip = payslipData?.data?.data;
  
  // Log para debug
  React.useEffect(() => {
    if (payslip) {
      console.log('📋 Holerite carregado no modal:', payslip);
      console.log('📋 absence_details:', payslip.absence_details);
      console.log('📋 diverse_expenses:', payslip.diverse_expenses);
      console.log('📋 extra_advances:', payslip.extra_advances);
    }
  }, [payslip]);

  if (!payslip) return null;

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Holerite</h2>
              <p className="text-sm text-gray-500">
                {monthNames[payslip.ref_month - 1]} / {payslip.ref_year}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  try {
                    pdfService.generatePayslipPDF(payslip);
                    toast.success('PDF do holerite gerado com sucesso!');
                  } catch (error) {
                    toast.error('Erro ao gerar PDF do holerite');
                    console.error(error);
                  }
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Baixar PDF
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Informações do funcionário */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Funcionário</p>
                  <p className="font-medium text-gray-900">{payslip.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Cargo</p>
                  <p className="font-medium text-gray-900">{payslip.role}</p>
                </div>
              </div>
            </div>

            {/* Horas Trabalhadas */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Horas Trabalhadas</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">Dias úteis</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {Number(payslip.regular_hours_weekday || 0).toFixed(2)} h
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    {Number(payslip.regular_salary_weekday || 0).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">Domingos</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {Number(payslip.regular_hours_weekend || 0).toFixed(2)} h
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    {Number(payslip.regular_salary_weekend || 0).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </p>
                </div>
              </div>
              {Number(payslip.fixed_saturday_amount || 0) > 0 && (
                <div className="mt-4 bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">Sábados (valor fixo)</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {Number(payslip.fixed_saturday_amount || 0).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </p>
                  {payslip.worked_saturdays && payslip.worked_saturdays.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-yellow-200">
                      <p className="text-xs text-gray-600 mb-2">Sábados trabalhados:</p>
                      <div className="flex flex-wrap gap-2">
                        {payslip.worked_saturdays.map((sat, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-1 rounded-md bg-yellow-100 text-yellow-800 text-xs font-medium"
                          >
                            {String(sat.day).padStart(2, '0')}/{String(sat.month).padStart(2, '0')}/{sat.year}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Horas Extras */}
            {(Number(payslip.overtime_hours_weekday || 0) > 0 || Number(payslip.overtime_hours_weekend || 0) > 0 || (payslip.overtime_details && payslip.overtime_details.length > 0)) && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Horas Extras</h3>
                
                {/* Resumo */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">Dias úteis</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {Number(payslip.overtime_hours_weekday || 0).toFixed(2)} h
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      {Number(payslip.overtime_salary_weekday || 0).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">Fins de semana</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {Number(payslip.overtime_hours_weekend || 0).toFixed(2)} h
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      {Number(payslip.overtime_salary_weekend || 0).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </p>
                  </div>
                </div>

                {/* Detalhamento dia a dia */}
                {payslip.overtime_details && payslip.overtime_details.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Detalhamento por dia:</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dia</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tempo</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor Hora</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Multiplicador</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Valor Total</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {payslip.overtime_details.map((detail, idx) => {
                            const formatTime = (minutes) => {
                              const hours = Math.floor(minutes / 60);
                              const mins = minutes % 60;
                              if (hours > 0 && mins > 0) {
                                return `${hours}h ${mins}min`;
                              } else if (hours > 0) {
                                return `${hours}h`;
                              } else {
                                return `${mins}min`;
                              }
                            };

                            return (
                              <tr key={idx} className={detail.isWeekend ? 'bg-orange-50' : 'bg-yellow-50'}>
                                <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                                  {String(detail.day).padStart(2, '0')}/{String(detail.month).padStart(2, '0')}/{detail.year}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                                    detail.isWeekend 
                                      ? 'bg-orange-100 text-orange-800' 
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {detail.dayName}
                                  </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                  {formatTime(detail.minutes)} ({detail.hours.toFixed(2)}h)
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                                  {detail.hourlyRate.toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  })}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                                  {detail.multiplier === 1.0 ? '1x' : detail.multiplier === 1.5 ? '1.5x (50%)' : '2x (100%)'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-right font-semibold text-gray-900">
                                  {(() => {
                                    const val = Number(detail.value || 0);
                                    return `R$ ${val.toFixed(3).replace('.', ',')}`;
                                  })()}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        {payslip.overtime_details && payslip.overtime_details.length > 0 && (
                          <tfoot className="bg-gray-100">
                            <tr>
                              <td colSpan="5" className="px-3 py-2 text-right font-semibold text-gray-900">
                                Total:
                              </td>
                              <td className="px-3 py-2 text-right font-bold text-gray-900">
                                {(() => {
                                  const total = payslip.overtime_details.reduce((sum, detail) => sum + Number(detail.value || 0), 0);
                                  return `R$ ${total.toFixed(3).replace('.', ',')}`;
                                })()}
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Divergências/Faltas Descontadas */}
            {payslip.absence_details && payslip.absence_details.length > 0 && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-red-700 mb-3">Divergências/Faltas Descontadas</h3>
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700 mb-3">
                    As seguintes divergências não justificadas foram descontadas do salário:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-red-200">
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Data</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Tipo</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Horas Descontadas</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Valor Descontado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payslip.absence_details.map((detail, idx) => {
                          const date = new Date(detail.date);
                          return (
                            <tr key={idx} className="border-b border-red-100">
                              <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                {date.toLocaleDateString('pt-BR')}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                {detail.type_description}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                {detail.hours.toFixed(2)}h
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-right font-semibold text-red-700">
                                - R$ {detail.value.toFixed(2).replace('.', ',')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-red-100">
                        <tr>
                          <td colSpan="3" className="px-3 py-2 text-right font-semibold text-gray-900">
                            Total Descontado:
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-red-700">
                            - R$ {(() => {
                              const total = payslip.absence_details.reduce((sum, detail) => sum + Number(detail.value || 0), 0);
                              return total.toFixed(2).replace('.', ',');
                            })()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Vales Extras Descontados */}
            {payslip.extra_advances && payslip.extra_advances.length > 0 && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-red-700 mb-3">Vales Extras Descontados</h3>
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700 mb-3">
                    Os seguintes vales extras foram descontados do salário:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-red-200">
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Data</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Descrição</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Valor Descontado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payslip.extra_advances.map((advance, idx) => {
                          const date = new Date(advance.request_date);
                          return (
                            <tr key={idx} className="border-b border-red-100">
                              <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                {date.toLocaleDateString('pt-BR')}
                              </td>
                              <td className="px-3 py-2 text-gray-700">
                                {advance.description || 'Vale extra'}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-right font-semibold text-red-700">
                                - R$ {Number(advance.amount).toFixed(2).replace('.', ',')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-red-100">
                        <tr>
                          <td colSpan="2" className="px-3 py-2 text-right font-semibold text-gray-900">
                            Total Descontado:
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-red-700">
                            - R$ {(() => {
                              const total = payslip.extra_advances.reduce((sum, advance) => sum + Number(advance.amount || 0), 0);
                              return total.toFixed(2).replace('.', ',');
                            })()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Despesas Diversas Descontadas */}
            {payslip.diverse_expenses && payslip.diverse_expenses.length > 0 && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-red-700 mb-3">Despesas Diversas Descontadas</h3>
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700 mb-3">
                    As seguintes parcelas de despesas diversas foram descontadas do salário:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-red-200">
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Tipo</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Descrição</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 uppercase">Parcela</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Valor Descontado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payslip.diverse_expenses.map((expense, idx) => (
                          <tr key={idx} className="border-b border-red-100">
                            <td className="px-3 py-2 whitespace-nowrap text-gray-700 font-medium">
                              {expense.expense_type_name || 'N/A'}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {expense.description || '-'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-center text-gray-700">
                              {expense.installment_number}/{expense.installments_count}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right font-semibold text-red-700">
                              - R$ {Number(expense.amount || 0).toFixed(2).replace('.', ',')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-red-100">
                        <tr>
                          <td colSpan="3" className="px-3 py-2 text-right font-semibold text-gray-900">
                            Total Descontado:
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-red-700">
                            - R$ {(() => {
                              const total = payslip.diverse_expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
                              return total.toFixed(2).replace('.', ',');
                            })()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Adicional Feriado */}
            {Number(payslip.holiday_bonus_amount || 0) > 0 && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Adicional Feriado</h3>
                <p className="text-sm text-gray-600 mb-1">
                  {Number(payslip.holiday_hours || 0).toFixed(2)} horas trabalhadas em feriados
                </p>
                <p className="text-xl font-medium text-gray-700">
                  {Number(payslip.holiday_bonus_amount).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </p>
              </div>
            )}

            {/* Adicional Mensal */}
            {Number(payslip.extra_monthly_amount || 0) > 0 && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Adicional Mensal</h3>
                <p className="text-xl font-medium text-gray-700">
                  {Number(payslip.extra_monthly_amount).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </p>
              </div>
            )}

            {/* Desconto de Adiantamento Quinzenal */}
            {Number(payslip.advance_deduction || 0) > 0 && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Desconto - Adiantamento Quinzenal</h3>
                <p className="text-xl font-medium text-red-600">
                  -{Number(payslip.advance_deduction).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </p>
              </div>
            )}

            {/* Total */}
            <div className="border-t-2 border-gray-300 pt-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Total a Receber</h3>
                <p className="text-3xl font-bold text-green-600">
                  {Number(payslip.total_salary || 0).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </p>
              </div>
            </div>

            {/* Informações de aprovação */}
            {payslip.approved_at && (
              <div className="border-t pt-4">
                <p className="text-xs text-gray-500">
                  Aprovado em {new Date(payslip.approved_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {payslip.approver_name && ` por ${payslip.approver_name}`}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyPayslips;

